if (!customElements.get('b2b-registration-form')) {
  customElements.define(
    'b2b-registration-form',
    class B2BRegistrationForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.spinner = this.submitButton?.querySelector('.loading__spinner');
        this.message = this.querySelector('.b2b-registration-form__message');
        this.turnstileWidget = this.querySelector('.cf-turnstile');

        this.endpoint = this.dataset.endpoint;
        this.successMessage = this.dataset.successMessage || 'Thanks. Your B2B registration has been submitted.';
        this.errorMessage = this.dataset.errorMessage || 'Something went wrong. Please try again.';
        this.captchaErrorMessage = this.dataset.captchaErrorMessage || 'Please complete the security check.';
        this.successRedirectUrl = this.dataset.successRedirectUrl || '';

        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        if (!this.form) return;

        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        if (!this.form) return;

        this.form.removeEventListener('submit', this.onSubmit);
      }

      async onSubmit(event) {
        event.preventDefault();

        if (!this.endpoint) {
          this.showMessage(this.errorMessage, 'error');
          return;
        }

        const formData = new FormData(this.form);
        const captchaToken = formData.get('cf-turnstile-response');

        if (this.turnstileWidget && !captchaToken) {
          this.showMessage(this.captchaErrorMessage, 'error');
          return;
        }

        const payload = this.getPayload(formData);

        this.setLoading(true);
        this.hideMessage();

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(payload),
          });

          const result = await this.safeParseJson(response);

          console.log('B2B registration response:', response.status, result);

          if (!response.ok || result?.success === false) {
            throw new Error(result?.message || this.errorMessage);
          }

          this.form.reset();
          this.resetTurnstile();

          this.dispatchEvent(
            new CustomEvent('b2b-registration:success', {
              bubbles: true,
              detail: {
                response: result,
                payload,
              },
            }),
          );

          if (this.successRedirectUrl) {
            window.location.assign(this.successRedirectUrl);
            return;
          }

          this.showMessage(result?.message || this.successMessage, 'success');
        } catch (error) {
          this.resetTurnstile();

          this.showMessage(error.message || this.errorMessage, 'error');

          this.dispatchEvent(
            new CustomEvent('b2b-registration:error', {
              bubbles: true,
              detail: {
                error,
                payload,
              },
            }),
          );
        } finally {
          this.setLoading(false);
        }
      }

      getPayload(formData) {
        const payload = {
          first_name: this.getFormValue(formData, 'first_name'),
          last_name: this.getFormValue(formData, 'last_name'),
          email: this.getFormValue(formData, 'email'),
          company: this.getFormValue(formData, 'company'),
          phone: this.getFormValue(formData, 'phone'),
          address: this.getFormValue(formData, 'address'),
          city: this.getFormValue(formData, 'city'),
          country: this.getFormValue(formData, 'country'),
          postcode: this.getFormValue(formData, 'postcode'),
          vat_number: this.getFormValue(formData, 'vat_number'),
          accepts_marketing: this.form.querySelector('[name="accepts_marketing"]')?.checked === true,
        };

        const captchaToken = formData.get('cf-turnstile-response');

        if (captchaToken) {
          payload['cf-turnstile-response'] = captchaToken;
        }

        return payload;
      }

      getFormValue(formData, name) {
        const value = formData.get(name);

        return typeof value === 'string' ? value.trim() : '';
      }

      async safeParseJson(response) {
        const contentType = response.headers.get('content-type');

        if (!contentType || !contentType.includes('application/json')) {
          return null;
        }

        return response.json();
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute('aria-busy', String(isLoading));
        this.submitButton.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
      }

      showMessage(message, type = 'success') {
        if (!this.message) return;

        this.message.hidden = false;
        this.message.textContent = message;
        this.message.classList.remove('is-success', 'is-error');
        this.message.classList.add(type === 'error' ? 'is-error' : 'is-success');

        this.message.setAttribute('role', type === 'error' ? 'alert' : 'status');
      }

      hideMessage() {
        if (!this.message) return;

        this.message.hidden = true;
        this.message.textContent = '';
        this.message.classList.remove('is-success', 'is-error');
        this.message.removeAttribute('role');
      }

      resetTurnstile() {
        if (window.turnstile && typeof window.turnstile.reset === 'function') {
          window.turnstile.reset();
        }
      }
    },
  );
}